/**
 * Categorization facade — Worker + Dexie async path on top of pure core.
 */
import { SPENDING_CATEGORIES } from '../data/spendingCategories';
import {
    KEYWORD_INDEX,
    KEYWORD_LIST,
    normalizeMerchant,
    matchCandidates,
    hasCommerceHint,
    looksLikePersonName,
    keywordMatch,
    categorizeMerchantSync,
    isUnnamedQr,
    getFuseEntries,
    escapeRegExp,
    SUBSCRIPTION_KEYWORDS,
    COMMERCE_HINTS,
    AMBIGUOUS_KEYWORDS,
    PSP_SUFFIXES,
} from './categorizationCore';
import { parseAmountAbs } from './parsers';
import { db } from './db';

export {
    KEYWORD_INDEX,
    KEYWORD_LIST,
    normalizeMerchant,
    matchCandidates,
    hasCommerceHint,
    looksLikePersonName,
    keywordMatch,
    categorizeMerchantSync,
    isUnnamedQr,
    COMMERCE_HINTS,
    AMBIGUOUS_KEYWORDS,
    PSP_SUFFIXES,
};

let categorizationWorker = null;
let fuseReady = false;

if (typeof window !== 'undefined' && window.Worker) {
    categorizationWorker = new Worker(new URL('../categorizationWorker.js', import.meta.url));
    categorizationWorker.onmessage = (e) => {
        if (e.data.type === 'READY') {
            fuseReady = true;
        }
    };
    categorizationWorker.postMessage({ type: 'INIT', entries: getFuseEntries() });
}

function fuseMatch(normalizedKey) {
    return new Promise((resolve) => {
        if (!categorizationWorker || !fuseReady) {
            resolve(null);
            return;
        }

        const id = Date.now() + Math.random().toString();
        const listener = (e) => {
            if (e.data.type === 'RESULT' && e.data.id === id) {
                categorizationWorker.removeEventListener('message', listener);
                const result = e.data.result;
                if (!result) {
                    resolve(null);
                    return;
                }
                // Reject fuzzy hits where keyword is much shorter than query
                const kw = String(result.keyword || '');
                const q = String(normalizedKey || '');
                if (kw.length < Math.max(4, Math.floor(q.length * 0.6))) {
                    resolve(null);
                    return;
                }
                resolve(result);
            }
        };
        categorizationWorker.addEventListener('message', listener);
        categorizationWorker.postMessage({ type: 'MATCH', query: normalizedKey, id });
    });
}

export async function categorizeMerchant(rawMerchant, userId, amount = 0, type = null) {
    const key = normalizeMerchant(rawMerchant);
    const rawLower = String(rawMerchant || '').toLowerCase();

    // Income narration (credits / salary strings) — before empty QR bail
    const syncEarly = categorizeMerchantSync(rawMerchant, amount, type);
    if (syncEarly.source === 'income') return syncEarly;

    if (!key || isUnnamedQr(key)) {
        return { category: 'Uncategorized', subcategory: 'Needs Review', confidence: 0, source: 'none' };
    }

    // T1: Personal override
    if (userId) {
        try {
            const override = await db.user_overrides.get([userId, key]);
            if (override) {
                return {
                    category: override.category,
                    subcategory: override.subcategory,
                    source: 'user',
                    confidence: 1.0,
                };
            }
        } catch {
            // IndexedDB unavailable — continue
        }
    }

    // T2: Local registry (filled by retags)
    let registry = null;
    try {
        registry = await db.merchant_registry.get(key);
        if (registry && registry.vote_count >= 1 && (registry.confidence ?? 0) >= 0.75) {
            return {
                category: registry.category,
                subcategory: registry.subcategory,
                source: 'registry',
                confidence: registry.confidence,
            };
        }
    } catch {
        // ignore
    }

    // T3: Subscriptions
    for (const indicator of SUBSCRIPTION_KEYWORDS) {
        if (/^(phonepe|gpay|google pay|paytm)$/i.test(String(indicator).trim())) continue;
        const pattern = new RegExp('\\b' + escapeRegExp(indicator) + '\\b', 'i');
        if (pattern.test(rawLower)) {
            return { category: 'Subscription', subcategory: 'Recurring Payment', confidence: 0.95, source: 'subscription' };
        }
    }

    // T4: Strong keyword
    const exact = keywordMatch(key);
    if (exact) {
        const soft = exact.matchType === 'contains' || exact.matchType === 'compact';
        return {
            category: exact.category,
            subcategory: exact.subcategory,
            confidence: soft ? 0.88 : 0.92,
            source: 'keyword',
        };
    }

    // T5: Fuzzy
    const fuzzy = await fuseMatch(key);
    if (fuzzy) {
        return {
            category: fuzzy.category,
            subcategory: fuzzy.subcategory,
            confidence: fuzzy.confidence ?? 0.7,
            source: 'fuzzy',
        };
    }

    // T6: P2P heuristic
    const amtNum = parseAmountAbs({ amount });
    if (
        amtNum >= 500
        && amtNum <= 99000
        && amtNum % 100 === 0
        && looksLikePersonName(key)
        && !hasCommerceHint(key)
    ) {
        return {
            category: 'Family & Friends',
            subcategory: 'Personal Transfer',
            confidence: 0.7,
            source: 'amount_heuristic',
        };
    }

    // T7: Weak registry
    if (registry) {
        return {
            category: registry.category,
            subcategory: registry.subcategory,
            source: 'registry_weak',
            confidence: registry.confidence,
        };
    }

    return { category: 'Uncategorized', subcategory: 'Needs Review', source: 'none', confidence: 0 };
}

export function categorizeTransaction(txn) {
    return categorizeMerchantSync(
        txn.party || txn.raw_merchant || '',
        txn.amount,
        txn.type
    );
}

export function getCategoryColorClass(category) {
    if (!category || category === 'Uncategorized') return 'uncategorized';
    var catLower = category.toLowerCase();
    if (catLower.includes('food') || catLower.includes('dining') || catLower.includes('grocer') || catLower.includes('zomato') || catLower.includes('swiggy')) return 'cat-food';
    if (catLower.includes('travel') || catLower.includes('fuel') || catLower.includes('uber') || catLower.includes('ola') || catLower.includes('petrol') || catLower.includes('transport')) return 'cat-travel';
    if (catLower.includes('shop') || catLower.includes('amazon') || catLower.includes('flipkart') || catLower.includes('myntra') || catLower.includes('beauty')) return 'cat-shopping';
    if (catLower.includes('bill') || catLower.includes('utility') || catLower.includes('recharge') || catLower.includes('electricity') || catLower.includes('rent')) return 'cat-bills';
    if (catLower.includes('invest') || catLower.includes('mutual') || catLower.includes('stock') || catLower.includes('sip') || catLower.includes('income')) return 'cat-investment';
    if (catLower.includes('movie') || catLower.includes('entertainment') || catLower.includes('netflix')) return 'cat-entertainment';
    if (catLower.includes('medical') || catLower.includes('pharmacy') || catLower.includes('doctor') || catLower.includes('hospital')) return 'cat-medical';
    if (catLower.includes('education') || catLower.includes('school') || catLower.includes('tuition')) return 'cat-salary';
    if (catLower.includes('donation') || catLower.includes('religion') || catLower.includes('family') || catLower.includes('transfer')) return 'cat-bills';
    if (catLower.includes('salary') || catLower.includes('interest')) return 'cat-salary';

    var colors = ['cat-food', 'cat-travel', 'cat-shopping', 'cat-bills', 'cat-investment', 'cat-entertainment', 'cat-medical', 'cat-salary'];
    var hash = 0;
    for (var i = 0; i < category.length; i++) {
        hash = category.charCodeAt(i) + ((hash << 5) - hash);
    }
    var index = Math.abs(hash) % colors.length;
    return colors[index];
}

export function getCategoryColor(category) {
    const colors = {
        'Food & Drinks': '#4CAF50',
        'Food & Dining': '#4CAF50',
        'Groceries': '#8BC34A',
        'Shopping': '#00BCD4',
        'Transport': '#03A9F4',
        'Financial Services': '#3F51B5',
        'Home': '#2196F3',
        'Medical': '#4DB6AC',
        'Entertainment': '#7986CB',
        'Bill': '#009688',
        'Subscription': '#64B5F6',
        'Subscriptions': '#64B5F6',
        'Personal Transfers': '#607D8B',
        'Transfers': '#78909C',
        'Income': '#66BB6A',
        'Personal': '#607D8B',
        'Services': '#81C784',
        'Fitness': '#4DB6AC',
        'Investment': '#3F51B5',
        'EMI': '#90A4AE',
        'Credit Bill': '#90A4AE',
        'Insurance': '#4DD0E1',
        'Tax': '#B0BEC5',
        'Family & Friends': '#7986CB',
        'Children': '#AED581',
        'Pets': '#81C784',
        'Business': '#4DD0E1',
        'Travel': '#03A9F4',
        'Education': '#5C6BC0',
        'Rent': '#00897B',
        'Beauty & Grooming': '#EC407A',
        'Donations & Religion': '#AB47BC',
        'Misc': '#78909C',
        'Uncategorized': '#B0BEC5'
    };
    return colors[category] || '#888888';
}

export function detectRecurring(transactions) {
    const subscriptionKeywords = [
        { name: 'Netflix', keywords: ['netflix'] },
        { name: 'Amazon Prime', keywords: ['amazon prime', 'prime video', 'primevideo'] },
        { name: 'Disney+ Hotstar', keywords: ['hotstar', 'disney', 'jiohotstar'] },
        { name: 'Spotify', keywords: ['spotify'] },
        { name: 'YouTube Premium', keywords: ['youtube premium', 'youtube music', 'google youtube'] },
        { name: 'Apple Music', keywords: ['apple music', 'apple.com/bill'] },
        { name: 'Zee5', keywords: ['zee5'] },
        { name: 'SonyLIV', keywords: ['sony liv', 'sonyliv'] },
        { name: 'JioSaavn', keywords: ['jiosaavn', 'jio saavn'] },
        { name: 'Gaana', keywords: ['gaana'] },
        { name: 'Swiggy One', keywords: ['swiggy one'] },
        { name: 'Zomato Pro', keywords: ['zomato pro', 'zomato gold'] },
        { name: 'Cult.fit', keywords: ['cult.fit', 'cultfit', 'curefit'] },
        { name: 'Times Prime', keywords: ['times prime'] },
        { name: 'Electricity Bill', keywords: ['electricity', 'bescom', 'tata power', 'adani electricity', 'reliance energy', 'msedcl'] },
        { name: 'Mobile Recharge', keywords: ['jio prepaid', 'airtel prepaid', 'vi prepaid', 'bsnl'] },
        { name: 'Internet/Broadband', keywords: ['jio fiber', 'airtel broadband', 'act fibernet', 'hathway'] },
        { name: 'Insurance', keywords: ['insurance', 'lic', 'hdfc life', 'icici prudential', 'sbi life', 'max life'] },
        { name: 'EMI/Loan', keywords: ['emi', 'loan', 'hdfc bank emi', 'icici emi', 'bajaj emi'] },
        { name: 'Mutual Fund SIP', keywords: ['sip', 'mutual fund', 'groww', 'zerodha', 'kuvera', 'coin'] }
    ];

    const grouped = {};
    const subscriptions = [];
    const seenMerchants = new Set();

    transactions.forEach(txn => {
        const party = (txn.party || '').toLowerCase();
        if (!party) return;

        for (const sub of subscriptionKeywords) {
            for (const kw of sub.keywords) {
                if (party.includes(kw) && !seenMerchants.has(sub.name)) {
                    seenMerchants.add(sub.name);
                    const matchingTxns = transactions.filter(t =>
                        sub.keywords.some(k => (t.party || '').toLowerCase().includes(k))
                    );
                    if (matchingTxns.length > 0) {
                        const amounts = matchingTxns.map(t => parseAmountAbs(t));
                        const avgAmount = amounts.reduce((a, b) => a + b, 0) / amounts.length;

                        matchingTxns.sort((A, B) => new Date(B.date) - new Date(A.date));
                        const latestDate = matchingTxns[0].date;

                        subscriptions.push({
                            merchant: sub.name,
                            originalName: txn.party,
                            count: matchingTxns.length,
                            avgAmount: avgAmount,
                            totalSpent: amounts.reduce((a, b) => a + b, 0),
                            isKnownSubscription: true,
                            type: 'subscription',
                            lastDate: latestDate
                        });
                    }
                    break;
                }
            }
        }

        if (!grouped[party]) grouped[party] = [];
        grouped[party].push(txn);
    });

    const recurring = [];
    for (const [party, txns] of Object.entries(grouped)) {
        if (txns.length < 2) continue;
        if (seenMerchants.has(party)) continue;

        const amounts = txns.map(t => parseAmountAbs(t));
        const avg = amounts.reduce((a, b) => a + b, 0) / amounts.length;
        const variance = amounts.every(a => Math.abs(a - avg) / (avg || 1) < 0.15);
        if (!variance) continue;

        txns.sort((A, B) => new Date(B.date) - new Date(A.date));
        recurring.push({
            merchant: txns[0].party,
            originalName: txns[0].party,
            count: txns.length,
            avgAmount: avg,
            totalSpent: amounts.reduce((a, b) => a + b, 0),
            isKnownSubscription: false,
            type: 'recurring',
            lastDate: txns[0].date
        });
    }

    return [...subscriptions, ...recurring].sort((a, b) => b.totalSpent - a.totalSpent);
}

export function getAllCategories() {
    const categories = Object.keys(SPENDING_CATEGORIES);
    if (!categories.includes('Uncategorized')) {
        categories.push('Uncategorized');
    }
    return categories;
}

export function getSubcategories(category) {
    if (category === 'Uncategorized') {
        return ['Unknown', 'Other', 'Needs Review'];
    }
    return SPENDING_CATEGORIES[category] ? Object.keys(SPENDING_CATEGORIES[category]) : ['General'];
}

/**
 * Re-run auto-categorization for IndexedDB rows that are not user overrides.
 */
export async function recategorizeStoredTransactions(userId = 'guest-user') {
    const rows = await db.transactions.toArray();
    const updates = [];

    for (const txn of rows) {
        const src = txn.category_source;
        if (src === 'user' || src === 'manual') continue;

        const catRes = await categorizeMerchant(
            txn.party || txn.raw_merchant || '',
            userId,
            txn.amount,
            txn.type
        );

        if (
            catRes.category === txn.category
            && catRes.subcategory === txn.subcategory
            && catRes.source === txn.category_source
        ) {
            continue;
        }

        updates.push({
            ...txn,
            category: catRes.category,
            subcategory: catRes.subcategory,
            confidence: catRes.confidence,
            category_source: catRes.source,
            merchant_key: normalizeMerchant(txn.party || txn.raw_merchant || ''),
        });
    }

    if (updates.length) {
        await db.transactions.bulkPut(updates);
    }
    return updates.length;
}
